import { useEffect, useState } from "react";
import { withKeys } from "../lib/keys";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Activity, ArrowLeft, ArrowRight } from "lucide-react";

export const Field = ({ label, id, value, onChange, multiline, unit, type = "text", placeholder, rows = 3 }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="cc-label">{label}{unit && <span className="normal-case tracking-normal font-normal ml-1">({unit})</span>}</Label>
    {multiline ? (
      <Textarea id={id} data-testid={`input-${id}`} rows={rows} value={value || ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} className="cc-input" />
    ) : (
      <Input id={id} data-testid={`input-${id}`} type={type} value={value ?? ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} className="cc-input" step="any" />
    )}
  </div>
);

export const StepHeader = ({ num, title, sub }) => (
  <div className="mb-8 cc-rise">
    <div className="cc-label text-[var(--cc-red)] mb-2">Step {num} of 8</div>
    <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">{title}</h1>
    {sub && <p className="text-muted-foreground mt-2 max-w-2xl">{sub}</p>}
  </div>
);

export const NavButtons = ({ onBack, onNext, nextLabel = "Continue", nextDisabled, loading }) => (
  <div className="flex items-center justify-between pt-8 mt-8 border-t">
    {onBack ? (
      <Button variant="ghost" data-testid="btn-prev-step" onClick={onBack} className="cc-btn-ghost"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
    ) : <span />}
    {onNext && (
      <Button data-testid="btn-next-step" disabled={nextDisabled || loading} onClick={onNext} className="cc-btn">
        {loading ? <Activity className="w-4 h-4 mr-2 cc-pulse" /> : null}{nextLabel}{!loading && <ArrowRight className="w-4 h-4 ml-2" />}
      </Button>
    )}
  </div>
);

export const AiLoader = ({ text }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    const h = (e) => setN(e.detail);
    window.addEventListener("cc-progress", h);
    return () => window.removeEventListener("cc-progress", h);
  }, []);
  return (
  <div data-testid="ai-loader" className="border rounded-md p-8 bg-card flex items-center gap-4">
    <div className="cc-ecg"><svg viewBox="0 0 120 30" className="w-28 h-8"><polyline points="0,15 30,15 38,4 46,26 54,15 120,15" /></svg></div>
    <div>
      <div className="font-medium">{text}</div>
      <div className="text-sm text-muted-foreground">Syndromic reasoning with Washington Manual algorithms & Vincent...</div>
      <div className="text-xs font-mono text-muted-foreground mt-1" data-testid="ai-progress">{n ? `Receiving analysis · ${Math.round(n / 4)} tokens` : "Connecting to AI..."}</div>
    </div>
  </div>
  );
};

const LK = { High: "cc-chip-critical", Moderate: "cc-chip-warn", Low: "cc-chip-ok" };

export const DdxList = ({ items = [], testid }) => (
  <ol className="divide-y border rounded-md bg-card" data-testid={testid}>
    {withKeys(items, (d) => d.diagnosis).map(([key, d], i) => (
      <li key={key} className="p-5 grid grid-cols-[2rem_1fr] gap-3 cc-rise" style={{ animationDelay: `${i * 60}ms` }} data-testid={`${testid}-item-${i}`}>
        <span className="font-mono text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{d.diagnosis}</span>
            <span className={`cc-chip ${LK[d.likelihood] || ""}`}>{d.likelihood}</span>
          </div>
          {d.supporting && <p className="text-sm mt-1"><span className="text-[var(--cc-green)] font-medium">For: </span>{d.supporting}</p>}
          {d.against && <p className="text-sm text-muted-foreground"><span className="font-medium">Against: </span>{d.against}</p>}
        </div>
      </li>
    ))}
  </ol>
);

export const BulletList = ({ items = [], className = "list-disc pl-5 space-y-1 text-sm" }) => (
  <ul className={className}>{withKeys(items).map(([key, x]) => <li key={key}>{x}</li>)}</ul>
);
